import { cn } from '../../lib/utils';
import React, { useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import Skeleton from 'react-loading-skeleton';
import { useDragToScroll } from '../../hooks/useDragToScroll';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { Checkbox } from '../ui/checkbox';

const DataTable = ({
  data = [],
  showTitleRow = true,
  columns = [],
  columnConfig = [], // Array of { id, label, defaultVisible }
  storageKey = 'datatable_visible_columns',
  title = '',
  loading = false,
  isDark = false,
  emptyStateText = 'No data found matching the current filters',
}) => {
  const getInitialVisibleColumns = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    return columnConfig.map(c => c.id);
  };

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);

  const { scrollContainerRef, topScrollRef, isDragging, scrollWidth, dragHandlers, topScrollHandler } = useDragToScroll([data, loading]);

  const toggleColumn = (id) => {
    setVisibleColumns(prev => {
      const next = prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id];
      try { sessionStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const filteredColumns = useMemo(
    () => columns.filter(col => visibleColumns.includes(col.id)),
    [columns, visibleColumns]
  );

  const dataColWidth = useMemo(() => {
    const dataColCount = filteredColumns.filter(col => col.id !== 'actions').length;
    return dataColCount > 0 ? `${(100 / dataColCount).toFixed(2)}%` : 'auto';
  }, [filteredColumns]);

  const table = useReactTable({
    data,
    columns: filteredColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const containerClasses = cn(
    "rounded-[16px] overflow-hidden border-solid border-[1px] transition-colors duration-200",
    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]', !showTitleRow ? '!rounded-t-none' : ''
  );

  const headerRowClasses = cn(
    "h-[68px] flex items-center justify-between transition-colors duration-200 border-b border-solid border-[1px]",
    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]'
  );

  const titleClasses = cn(
    "m-0 font-bold text-[16px] transition-colors duration-200",
    isDark ? 'text-white' : 'text-[#072837]'
  );

  const tableHeaderBg  = isDark ? 'bg-[#1c1c1c]' : 'bg-[#072837]';
  const tableHeaderText = isDark ? 'text-white' : 'text-white';
  const rowBorderColor  = isDark ? 'border-[#292929]' : 'border-[#d9d9d9]';
  const emptyStateTextColor = isDark ? 'text-[#8c8c8c]' : 'text-[#686868]';

  const skeletonBaseColor      = isDark ? '#1f1f1f' : '#e9e9e9';
  const skeletonHighlightColor = isDark ? '#2a2a2a' : '#f5f5f5';

  return (
    <div className={cn("flex flex-col gap-[16px]")}>
      <div className={containerClasses}>
        {/* Title row */}
        {showTitleRow && (
          <div className={headerRowClasses}>
            <div className={cn("px-[24px]")}>
              <h2 className={titleClasses}>{title}</h2>
            </div>

          {/* Columns picker */}
            <div className={cn("px-[24px]")}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-[8px] px-[16px] py-[8px] rounded-[12px] border-solid border-[1px] text-[16px] font-semibold cursor-pointer transition-colors duration-200",
                      isDark
                        ? 'bg-[#000000] border-none text-[#b3b3b3] hover:bg-[#292929]'
                        : 'bg-[#f5f5f5] border-none text-[#686868] hover:bg-[#ebebeb]'
                    )}
                  >
                    <img src="/assets/icons/columns.svg" alt="" className={cn("w-[16px] h-[16px]")} style={{ filter: isDark ? 'invert(1)' : 'none' }} />
                    Columns
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className={cn(
                    "min-w-[210px] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto rounded-[12px] p-[6px] shadow-[0px_8px_24px_rgba(0,0,0,0.12)] border-[1px] [&::-webkit-scrollbar]:w-[4px] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full",
                    isDark
                      ? 'bg-[#161515] border-[#292929] [&::-webkit-scrollbar-track]:bg-[#292929] [&::-webkit-scrollbar-thumb]:bg-[#555555] [&::-webkit-scrollbar-thumb]:hover:bg-[#777777]'
                      : 'bg-white border-[#d9d9d9] [&::-webkit-scrollbar-track]:bg-[#f0f0f0] [&::-webkit-scrollbar-thumb]:bg-[#cccccc] [&::-webkit-scrollbar-thumb]:hover:bg-[#aaaaaa]'
                  )}
                >
                  {columnConfig.map(col => (
                    <DropdownMenuItem
                      key={col.id}
                      onSelect={(e) => { e.preventDefault(); toggleColumn(col.id); }}
                      className={cn("flex items-center gap-[10px] px-[12px] py-[10px] rounded-[8px] cursor-pointer focus:!bg-transparent")}
                    >
                      <Checkbox
                        checked={visibleColumns.includes(col.id)}
                        isDark={isDark}
                        onCheckedChange={() => toggleColumn(col.id)}
                      />
                      <span className={cn("text-[14px] font-medium select-none", isDark ? 'text-[#ffffff]' : 'text-[#072837]')}>
                        {col.label}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
        <div
          ref={topScrollRef}
          onScroll={topScrollHandler}
          className={cn(
            "overflow-x-auto [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px]",
            isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'
          )}
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
        <div
          ref={scrollContainerRef}
          {...dragHandlers}
          className={cn(
            "overflow-x-auto select-none [&_button]:cursor-pointer [&_a]:cursor-pointer [&::-webkit-scrollbar]:h-[10px] [&::-webkit-scrollbar]:w-[16px] [&::-webkit-scrollbar-button]:w-[16px] [&::-webkit-scrollbar-button]:h-[18px] [&::-webkit-scrollbar]:cursor-default [&::-webkit-scrollbar-thumb]:cursor-default [&::-webkit-scrollbar-track]:cursor-default [&::-webkit-scrollbar-button]:cursor-default",
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
            isDark ? '[&::-webkit-scrollbar-track]:bg-[#000000] [&::-webkit-scrollbar-thumb]:bg-[#292929] [&::-webkit-scrollbar-thumb]:hover:bg-[#4d4d4d] [&::-webkit-scrollbar-button]:bg-[#161515]' : '[&::-webkit-scrollbar-track]:bg-[#f5f5f5] [&::-webkit-scrollbar-thumb]:bg-[#ebebeb] [&::-webkit-scrollbar-thumb]:hover:bg-[#e5e5e5] [&::-webkit-scrollbar-button]:bg-[#d9d9d9]'
          )}
        >
          <table className={cn("w-full")}>
            <thead className={tableHeaderBg}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn("h-[57px] px-[24px] text-left font-bold text-[16px] text-white whitespace-nowrap transition-colors duration-200", tableHeaderText)}
                      style={header.id === 'actions' ? { width: '1%', whiteSpace: 'nowrap' } : { width: dataColWidth }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={filteredColumns.length} className={cn("p-[16px]")}>
                    <Skeleton
                      count={10}
                      height={57}
                      baseColor={skeletonBaseColor}
                      highlightColor={skeletonHighlightColor}
                    />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={cn(
                      "border-b border-solid last:border-b-0",
                      rowBorderColor,
                      isDark
                        ? index % 2 === 0 ? 'bg-[#161515]' : 'bg-[#1c1c1c]'
                        : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={cn("h-[57px] px-[24px] py-[10px] align-middle whitespace-nowrap transition-colors duration-200")}
                        style={cell.column.id === 'actions' ? { width: '1%', whiteSpace: 'nowrap' } : { width: dataColWidth }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={filteredColumns.length}
                    className={cn("px-[24px] py-[40px] text-center font-medium text-[14px]", emptyStateTextColor)}
                  >
                    {emptyStateText}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
